import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { gamificationService } from '../../lib/gamificationService';

import ActiveChallenges from './components/ActiveChallenges';
import LeaderboardPanel from './components/LeaderboardPanel';
import ChallengeCreation from './components/ChallengeCreation';
import TeamFormation from './components/TeamFormation';
import AchievementSharing from './components/AchievementSharing';
import PrizeManagement from './components/PrizeManagement';

const SocialChallengesLeaderboards = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('challenges');
  const [challenges, setChallenges] = useState([]);
  const [leaderboards, setLeaderboards] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const gymId = userProfile?.gym_id;
  const userId = userProfile?.id;

  useEffect(() => {
    if (gymId && userId) {
      loadDashboardData();
    }
  }, [gymId, userId, refreshTrigger]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load active challenges
      const { data: challengesData } = await gamificationService?.getChallenges(gymId, 'active');
      setChallenges(challengesData || []);

      // Load leaderboard
      const { data: leaderboardData } = await gamificationService?.getLeaderboard(
        gymId,
        'points',
        'monthly',
        50
      );
      setLeaderboards(leaderboardData || []);

      // Load user rank
      const { data: rankData } = await gamificationService?.getUserRank(
        userId,
        gymId,
        'points',
        'monthly'
      );
      setUserRank(rankData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const tabs = [
    { id: 'challenges', label: 'Active Challenges', icon: 'Target' },
    { id: 'leaderboard', label: 'Leaderboards', icon: 'Trophy' },
    { id: 'create', label: 'Create Challenge', icon: 'PlusCircle' },
    { id: 'teams', label: 'Team Formation', icon: 'Users' },
    { id: 'achievements', label: 'Achievement Sharing', icon: 'Award' },
    { id: 'prizes', label: 'Prize Management', icon: 'Gift' }
  ];

  const stats = [
    {
      label: 'Active Challenges',
      value: challenges?.length || 0,
      icon: 'Target',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      label: 'Your Rank',
      value: userRank?.rank_position ? `#${userRank?.rank_position}` : 'N/A',
      icon: 'TrendingUp',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      label: 'Total Participants',
      value: leaderboards?.length || 0,
      icon: 'Users',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      label: 'Your Score',
      value: userRank?.score_value || 0,
      icon: 'Award',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Social Challenges & Leaderboards | Gym Management</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Social Challenges & Leaderboards</h1>
                <p className="text-gray-600 mt-1">Compete, collaborate, and celebrate fitness achievements</p>
              </div>
              <Button onClick={handleRefresh} className="flex items-center gap-2">
                <Icon name="RefreshCw" className="w-4 h-4" />
                Refresh
              </Button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {stats?.map((stat, index) => (
                <div key={index} className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{stat?.label}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{stat?.value}</p>
                    </div>
                    <div className={`${stat?.bgColor} p-3 rounded-lg`}>
                      <Icon name={stat?.icon} className={`w-6 h-6 ${stat?.color}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Tabs */}
          <DashboardNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Content Area */}
          <div className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
              </div>
            ) : (
              <>
                {activeTab === 'challenges' && (
                  <ActiveChallenges
                    challenges={challenges}
                    userId={userId}
                    onRefresh={handleRefresh}
                  />
                )}
                {activeTab === 'leaderboard' && (
                  <LeaderboardPanel
                    leaderboards={leaderboards}
                    userRank={userRank}
                    gymId={gymId}
                    userId={userId}
                    onRefresh={handleRefresh}
                  />
                )}
                {activeTab === 'create' && (
                  <ChallengeCreation
                    gymId={gymId}
                    userId={userId}
                    onRefresh={handleRefresh}
                  />
                )}
                {activeTab === 'teams' && (
                  <TeamFormation
                    challenges={challenges?.filter(c => c?.challenge_type === 'team')}
                    userId={userId}
                    onRefresh={handleRefresh}
                  />
                )}
                {activeTab === 'achievements' && (
                  <AchievementSharing
                    userId={userId}
                    gymId={gymId}
                  />
                )}
                {activeTab === 'prizes' && (
                  <PrizeManagement
                    challenges={challenges}
                    gymId={gymId}
                    onRefresh={handleRefresh}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SocialChallengesLeaderboards;